import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

// Services
import { RouteParam, ServiceBase } from '../service-base';

// Modèles
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { ImportBatch } from 'src/app/@shared/models/imports/import-batch.model';
import { ImportFormatSchema } from 'src/app/@shared/models/imports/import-column-def';
import { ImportRow } from 'src/app/@shared/models/imports/import-row.model';

// Utilitaires
import { ODataResponse } from 'src/app/@shared/utilities/odata/response';
import { QueryOptions } from 'src/app/@shared/utilities/odata/query-params';

const CLASS_NAME = 'ImportBatch';
const ENDPOINT = `/imports/batches/`;
const SCHEMA_ENDPOINT = `/imports/formats/{code}/schema/`;
const ROUTES: RouteParam[] = [
    { key: 'finalize', path: '{id}/finalize/' },
    { key: 'rows', path: '{id}/rows/' },
    { key: 'rowDetail', path: '{id}/rows/{rowId}/' },
];

@Injectable({
    providedIn: 'root'
})
export class ImportBatchService extends ServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addRoutes(ROUTES);
    }

    public deleteRow$(batchId: number, rowId: number): Observable<boolean> {
        const url = this.routes['rowDetail'].replace('{id}', batchId.toString()).replace('{rowId}', rowId.toString());
        return this.httpClient.delete(url).pipe(
            map(() => true),
            catchError((error: any) => {
                this.handleError(error);
                return of(false);
            })
        );
    }

    public finalize$(id: number): Observable<ImportBatch> {
        const url = this.routes['finalize'].replace('{id}', id.toString());
        return this._postAction$<ImportBatch>(url);
    }

    public getRows$(batchId: number, skip: number = 0, top: number | null = null): Observable<{ rows: ImportRow[]; count: number }> {
        const baseUrl = this.routes['rows'].replace('{id}', batchId.toString());
        const queryOptions = new QueryOptions(baseUrl, { skip: skip, top: top, count: true });
        return this.httpClient.get(queryOptions.toUrlString()).pipe(
            map((response: any) => ({
                rows: (response.results ?? []).map((row: any) => GeneralFactory.get('ImportRow', row) as ImportRow),
                count: response.count ?? 0
            }))
        );
    }

    public getSchema$(code: string): Observable<ImportFormatSchema> {
        const url = SCHEMA_ENDPOINT.replace('{code}', code);
        return this.httpClient.get(url).pipe(
            map((response: any) => response as ImportFormatSchema)
        );
    }

    public updateRow$(batchId: number, rowId: number, payload: { data?: { [key: string]: any }; selected?: boolean }): Observable<ImportRow> {
        const url = this.routes['rowDetail'].replace('{id}', batchId.toString()).replace('{rowId}', rowId.toString());
        return this.httpClient.put(url, payload).pipe(
            map((response: any) => {
                const odata = new ODataResponse(response);
                this.messageService.sendODataMessages(odata.messages);
                return GeneralFactory.get('ImportRow', odata.value) as ImportRow;
            }),
            catchError((error: any) => {
                this.handleError(error);
                return of(null as any as ImportRow);
            })
        );
    }
}
